import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  CircularProgress,
  Alert,
  DialogActions,
  Button,
  Breadcrumbs,
  Link,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RouterIcon from '@mui/icons-material/Router';
import CloseIcon from '@mui/icons-material/Close';
import OptimizationRunDetailsDialog from './OptimizationRunDetailsDialog';
import { orderService } from '../services/order.service';
import { optimizationService } from '../services/optimization.service';
import { forecastRunsService } from '../services/forecastruns.service';

const Orders = () => {
    const [rowData, setRowData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPO, setSelectedPO] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    
    // Add state for optimization run dialog
    const [selectedOptRunId, setSelectedOptRunId] = useState(null);
    const [optRunOpen, setOptRunOpen] = useState(false);
    
    // Add state for planning workflow data
    const [planningWorkflowData, setPlanningWorkflowData] = useState({
        forecast: null,
        optimization: null,
        sourcing: null,
        po_generation: null,
        po_gateway: null
    });
    const [workflowLoading, setWorkflowLoading] = useState(false);
    
    // Add state for configuration dialog
    const [configDialogOpen, setConfigDialogOpen] = useState(false);
    const [configDialogTitle, setConfigDialogTitle] = useState('');
    const [configDialogContent, setConfigDialogContent] = useState(null);

    // Add table state for sorting and filtering
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('order_date');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [filters, setFilters] = useState({
        po_number: '',
        store_name: '',
        supplier_name: '',
        status: ''
    });

    // Get unique values for filters
    const uniqueStatuses = useMemo(() => {
        const statuses = new Set(rowData.map(row => row.status));
        return Array.from(statuses).filter(Boolean);
    }, [rowData]);

    const uniqueStores = useMemo(() => {
        const stores = new Set(rowData.map(row => row.store?.store_name));
        return Array.from(stores).filter(Boolean);
    }, [rowData]);

    const uniqueSuppliers = useMemo(() => {
        const suppliers = new Set(rowData.map(row => row.supplier?.supplier_name));
        return Array.from(suppliers).filter(Boolean);
    }, [rowData]);

    // Utility functions for sorting and filtering
    function descendingComparator(a, b, orderBy) {
        // Handle nested object properties
        let aValue, bValue;
        
        if (orderBy === 'order_date') {
            aValue = a.dates?.order_date ? new Date(a.dates.order_date).getTime() : 0;
            bValue = b.dates?.order_date ? new Date(b.dates.order_date).getTime() : 0;
        } else if (orderBy === 'store_id') {
            aValue = a.store?.store_id || '';
            bValue = b.store?.store_id || '';
        } else if (orderBy === 'store_name') {
            aValue = a.store?.store_name || '';
            bValue = b.store?.store_name || '';
        } else if (orderBy === 'supplier_name') {
            aValue = a.supplier?.supplier_name || '';
            bValue = b.supplier?.supplier_name || '';
        } else if (orderBy === 'total_amount') {
            aValue = a.financial?.total_amount || 0;
            bValue = b.financial?.total_amount || 0;
        } else if (orderBy === 'total_ordered') {
            aValue = a.items?.total_ordered || 0;
            bValue = b.items?.total_ordered || 0;
        } else if (orderBy === 'total_shipped') {
            aValue = a.items?.total_shipped || 0;
            bValue = b.items?.total_shipped || 0;
        } else if (orderBy === 'total_received') {
            aValue = a.items?.total_received || 0;
            bValue = b.items?.total_received || 0;
        } else {
            // Default to direct property access
            aValue = a[orderBy] || '';
            bValue = b[orderBy] || '';
        }

        if (bValue < aValue) {
            return -1;
        }
        if (bValue > aValue) {
            return 1;
        }
        return 0;
    }

    function getComparator(order, orderBy) {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    }

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPage(0); // Reset to first page when filtering
    };

    const clearFilters = () => {
        setFilters({
            po_number: '',
            store_name: '',
            supplier_name: '',
            status: ''
        });
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Filter and sort data
    const filteredAndSortedData = useMemo(() => {
        let filtered = rowData.filter(row => {
            const matchesPO = !filters.po_number || 
                row.po_number?.toLowerCase().includes(filters.po_number.toLowerCase());
            const matchesStore = !filters.store_name || 
                row.store?.store_name?.toLowerCase().includes(filters.store_name.toLowerCase());
            const matchesSupplier = !filters.supplier_name || 
                row.supplier?.supplier_name?.toLowerCase().includes(filters.supplier_name.toLowerCase());
            const matchesStatus = !filters.status || row.status === filters.status;

            return matchesPO && matchesStore && matchesSupplier && matchesStatus;
        });

        return filtered.sort(getComparator(order, orderBy));
    }, [rowData, filters, order, orderBy]);

    // Paginate data
    const paginatedData = useMemo(() => {
        const startIndex = page * rowsPerPage;
        return filteredAndSortedData.slice(startIndex, startIndex + rowsPerPage);
    }, [filteredAndSortedData, page, rowsPerPage]);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const data = await orderService.getOrders();
            
            if (data.status === 'success') {
                setRowData(data.summaries);
            } else {
                setError(data.message || 'Failed to fetch orders');
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch orders');
            console.error('Error fetching orders:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPODetails = async (poNumber) => {
        try {
            setDetailsLoading(true);
            setDetailsError(null);
            
            console.log('🔍 Fetching PO details for:', poNumber);
            const data = await orderService.getPODetails(poNumber);
            console.log('📋 PO details response:', data);
            
            if (data.status === 'success') {
                setSelectedPO(data);
                setDetailsOpen(true);
                
                const optimizationRunId = data.data?.header?.optimization_run_id;
                console.log('🔗 Optimization Run ID from PO:', optimizationRunId);
                
                // Fetch planning workflow data if optimization run ID is available
                if (optimizationRunId) {
                    console.log('🚀 Starting workflow data fetch for:', optimizationRunId);
                    await fetchPlanningWorkflowData(optimizationRunId, data.data?.header);
                } else {
                    console.warn('⚠️ No optimization run ID found in PO data');
                    // Set basic workflow data with just the PO information
                    setPlanningWorkflowData({
                        forecast: null,
                        optimization: null,
                        sourcing: null,
                        po_generation: {
                            run_id: 'N/A',
                            po_count: 1,
                            status: data.data?.header?.status || 'UNKNOWN',
                            config: null
                        },
                        po_gateway: {
                            status: (data.data?.header?.status === 'SENT' || 
                                    data.data?.header?.status === 'ACKNOWLEDGED' || 
                                    data.data?.header?.status === 'SHIPPED' || 
                                    data.data?.header?.status === 'RECEIVED') ? 'COMPLETED' : 'PENDING',
                            released_at: data.data?.header?.released_at || data.data?.header?.updated_at || null,
                            gateway_status: data.data?.header?.status || 'NOT_SENT'
                        }
                    });
                }
            } else {
                setDetailsError(data.message || 'Failed to fetch PO details');
            }
        } catch (err) {
            setDetailsError(err.message || 'Error fetching PO details');
            console.error('💥 Error fetching PO details:', err);
        } finally {
            setDetailsLoading(false);
        }
    };

    const fetchPlanningWorkflowData = async (optimizationRunId, poHeader = null) => {
        try {
            setWorkflowLoading(true);
            console.log('🔍 Fetching planning workflow data for optimization run:', optimizationRunId);
            
            // Fetch optimization run details
            const optimizationData = await optimizationService.getOptimizationRunById(optimizationRunId);
            console.log('📊 Optimization API response:', optimizationData);
            
            // The API response is already the data, no need to access .data
            if (optimizationData && optimizationData.run_metadata) {
                const optRun = optimizationData;
                console.log('✅ Optimization run details loaded:', optRun);
                
                // Fetch forecast run details if available
                let forecastData = null;
                if (optRun.configuration?.forecast_run_id) {
                    console.log('🌤️ Fetching forecast run details for:', optRun.configuration.forecast_run_id);
                    try {
                        const forecastResponse = await forecastRunsService.getForecastRunById(optRun.configuration.forecast_run_id);
                        console.log('📈 Forecast API response:', forecastResponse);
                        
                        // Check if forecast response has data property and map it correctly
                        if (forecastResponse.status === 'success' && forecastResponse.data) {
                            const forecastRunData = forecastResponse.data;
                            forecastData = {
                                run_id: optRun.configuration.forecast_run_id, // Use the run ID from optimization config
                                algorithm_name: forecastRunData.algorithm_name,
                                algorithm_version: forecastRunData.algorithm_version,
                                status: 'COMPLETED', // Assuming completed if data exists
                                run_timestamp: forecastRunData.run_timestamp,
                                forecast_grain: forecastRunData.forecast_grain,
                                forecast_start_date: forecastRunData.forecast_start_date,
                                forecast_end_date: forecastRunData.forecast_end_date,
                                parameters: forecastRunData.parameters,
                                // These would need to be fetched separately or calculated
                                sku_count: 'N/A',
                                store_count: 'N/A'
                            };
                        } else {
                            console.warn('⚠️ Forecast response format unexpected:', forecastResponse);
                        }
                    } catch (error) {
                        console.warn('⚠️ Failed to fetch forecast run details:', error);
                    }
                } else {
                    console.warn('⚠️ No forecast run ID found in optimization configuration');
                    // Set basic forecast data from optimization config if available
                    if (optRun.configuration?.forecast_run_id) {
                        forecastData = {
                            run_id: optRun.configuration.forecast_run_id,
                            algorithm_name: 'Unknown',
                            algorithm_version: 'Unknown',
                            status: 'UNKNOWN',
                            run_timestamp: null,
                            forecast_grain: 'Unknown',
                            forecast_start_date: null,
                            forecast_end_date: null,
                            parameters: null,
                            sku_count: 'N/A',
                            store_count: 'N/A'
                        };
                    }
                }
                
                // Map the actual API response structure to workflow data
                const workflowData = {
                    forecast: forecastData,
                    optimization: {
                        run_metadata: {
                            inventory_run_id: optRun.run_metadata.inventory_run_id,
                            status: optRun.run_metadata.status,
                            created_at: optRun.run_metadata.created_at,
                            completed_at: optRun.run_metadata.completed_at,
                            processing_time_seconds: optRun.run_metadata.processing_time_seconds
                        },
                        scope: {
                            store_count: optRun.scope.store_count,
                            active_products_count: optRun.scope.active_products_count,
                            total_skus_processed: optRun.scope.total_skus_processed,
                            successful_skus_count: optRun.scope.successful_skus_count,
                            error_skus_count: optRun.scope.error_skus_count
                        },
                        configuration: {
                            forecast_run_id: optRun.configuration.forecast_run_id,
                            inventory_parameters: optRun.configuration.inventory_parameters,
                            sourcing_config: optRun.configuration.sourcing_config
                        },
                        results: optRun.results
                    },
                    sourcing: {
                        run_id: optimizationRunId,
                        config: optRun.configuration.sourcing_config,
                        results_path: optRun.results?.sourcing_results_path || null,
                        algorithm: optRun.configuration.sourcing_config?.global_defaults?.sourcing_algorithm,
                        grouping: optRun.configuration.sourcing_config?.po_generation_config?.grouping_strategy,
                        lead_time: optRun.configuration.sourcing_config?.po_generation_config?.default_lead_time_days,
                        released_at: optRun.run_metadata?.completed_at || optRun.run_metadata?.created_at
                    },
                    po_generation: {
                        run_id: optimizationRunId,
                        po_count: 0, // This would need to be fetched from PO service
                        status: poHeader ? poHeader.status : 'COMPLETED', // Use actual PO status
                        config: optRun.configuration.sourcing_config?.po_generation_config || null,
                        released_at: poHeader ? poHeader.created_at : (optRun.run_metadata?.completed_at || optRun.run_metadata?.created_at)
                    },
                    po_gateway: {
                        status: poHeader ? (poHeader.status === 'SENT' || 
                                           poHeader.status === 'ACKNOWLEDGED' || 
                                           poHeader.status === 'SHIPPED' || 
                                           poHeader.status === 'RECEIVED') ? 'COMPLETED' : 'PENDING' : 'PENDING',
                        released_at: poHeader ? poHeader.updated_at : null,
                        gateway_status: poHeader ? poHeader.status : 'NOT_SENT',
                        config: null
                    }
                };
                
                console.log('🎯 Setting planning workflow data:', workflowData);
                setPlanningWorkflowData(workflowData);
            } else {
                console.error('❌ Optimization data fetch failed or invalid format:', optimizationData);
                // Set basic workflow data with just the optimization run ID
                setPlanningWorkflowData({
                    forecast: null,
                    optimization: {
                        run_metadata: { inventory_run_id: optimizationRunId, status: 'UNKNOWN' },
                        scope: { store_count: 0, active_products_count: 0 },
                        configuration: { forecast_run_id: null }
                    },
                    sourcing: {
                        run_id: optimizationRunId,
                        config: null,
                        results_path: null,
                        released_at: null
                    },
                    po_generation: {
                        run_id: optimizationRunId,
                        po_count: 0,
                        status: 'UNKNOWN',
                        config: null,
                        released_at: null
                    },
                    po_gateway: {
                        status: poHeader ? (poHeader.status === 'SENT' || 
                                           poHeader.status === 'ACKNOWLEDGED' || 
                                           poHeader.status === 'SHIPPED' || 
                                           poHeader.status === 'RECEIVED') ? 'COMPLETED' : 'PENDING' : 'UNKNOWN',
                        released_at: poHeader ? (poHeader.released_at || poHeader.updated_at) : null,
                        gateway_status: poHeader ? poHeader.status : 'UNKNOWN',
                        config: null
                    }
                });
            }
        } catch (error) {
            console.error('💥 Error fetching planning workflow data:', error);
            // Set basic workflow data even on error
            setPlanningWorkflowData({
                forecast: null,
                optimization: {
                    run_metadata: { inventory_run_id: optimizationRunId, status: 'ERROR' },
                    scope: { store_count: 0, active_products_count: 0 },
                    configuration: { forecast_run_id: null }
                },
                sourcing: {
                    run_id: optimizationRunId,
                    config: null,
                    results_path: null,
                    released_at: null
                },
                po_generation: {
                    run_id: optimizationRunId,
                    po_count: 0,
                    status: 'ERROR',
                    config: null,
                    released_at: null
                },
                po_gateway: {
                    status: poHeader ? (poHeader.status === 'SENT' || 
                                       poHeader.status === 'ACKNOWLEDGED' || 
                                       poHeader.status === 'SHIPPED' || 
                                       poHeader.status === 'RECEIVED') ? 'COMPLETED' : 'PENDING' : 'ERROR',
                    released_at: poHeader ? (poHeader.released_at || poHeader.updated_at) : null,
                    gateway_status: poHeader ? poHeader.status : 'ERROR',
                    config: null
                }
            });
        } finally {
            setWorkflowLoading(false);
        }
    };

    // Function to handle opening the optimization run dialog
    const handleOpenOptRunDialog = (runId) => {
        setSelectedOptRunId(runId);
        setOptRunOpen(true);
    };

    // Function to handle closing the optimization run dialog
    const handleCloseOptRunDialog = () => {
        setOptRunOpen(false);
    };

    // Define the OptimizationRunRenderer BEFORE it's used in columnDefs
    const OptimizationRunRenderer = (params) => {
        if (!params.value) return '-';
        
        return (
            <span 
                style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => handleOpenOptRunDialog(params.value)}
            >
                {params.value}
            </span>
        );
    };

    // Status color mapping
    const statusColors = {
        'DRAFT': '#9e9e9e',      // Grey
        'PREPARED': '#bebebe',   // Grey
        'CONFIRMED': '#2196f3',   // Blue
        'SHIPPED': '#ff9800',     // Orange
        'RECEIVED': '#4caf50',    // Green
        'SENT': '#2196f3',    // BLUE
        'CANCELLED': '#f44336'    // Red
    };

    // Define table columns
    const columns = [
        { id: 'po_number', label: 'PO Number', minWidth: 130, sortable: true, filterable: true },
        { id: 'store_id', label: 'Store ID', minWidth: 130, sortable: true, filterable: false },
        { id: 'store_name', label: 'Store Name', minWidth: 180, sortable: true, filterable: true },
        { id: 'supplier_name', label: 'Supplier', minWidth: 150, sortable: true, filterable: true },
        { id: 'order_date', label: 'Order Date', minWidth: 160, sortable: true, filterable: false },
        { id: 'status', label: 'Status', minWidth: 130, sortable: true, filterable: true },
        { id: 'total_amount', label: 'Total Amount', minWidth: 130, sortable: true, filterable: false },
        { id: 'total_ordered', label: 'Items Ordered', minWidth: 130, sortable: true, filterable: false },
        { id: 'total_shipped', label: 'Items Shipped', minWidth: 130, sortable: true, filterable: false },
        { id: 'total_received', label: 'Items Received', minWidth: 130, sortable: true, filterable: false },
        { id: 'optimization_run_id', label: 'Optimization Run', minWidth: 200, sortable: true, filterable: false },
        { id: 'events', label: 'Events', minWidth: 300, sortable: false, filterable: false }
    ];

    // Planning workflow step configuration
    const planningSteps = [
        {
            label: 'Forecasting',
            icon: <TrendingUpIcon />,
            color: '#2196f3',
            key: 'forecast'
        },
        {
            label: 'Inventory Optimization',
            icon: <InventoryIcon />,
            color: '#4caf50',
            key: 'optimization'
        },
        {
            label: 'Supplier Sourcing',
            icon: <LocalShippingIcon />,
            color: '#ff9800',
            key: 'sourcing'
        },
        {
            label: 'PO Generation',
            icon: <ShoppingCartIcon />,
            color: '#9c27b0',
            key: 'po_generation'
        },
        {
            label: 'PO Gateway',
            icon: <RouterIcon />,
            color: '#607d8b',
            key: 'po_gateway'
        }
    ];

    // Status cell renderer
    const StatusCell = (props) => {
        const status = props.value;
        return (
            <Chip
                label={status}
                size="small"
                sx={{
                    backgroundColor: statusColors[status] || '#9e9e9e',
                    color: 'white',
                    '& .MuiChip-label': {
                        fontWeight: 500
                    }
                }}
            />
        );
    };

    // Enhanced PODetailsDialog with Planning Workflow Tracer
    const PODetailsDialog = ({ open, onClose, poDetails, loading, error }) => {
        const [activeStep, setActiveStep] = useState(0);
        const [expandedStep, setExpandedStep] = useState(null);

        const handleStepClick = (stepIndex) => {
            setActiveStep(stepIndex);
            setExpandedStep(expandedStep === stepIndex ? null : stepIndex);
        };

        const handleShowConfig = (title, content) => {
            setConfigDialogTitle(title);
            setConfigDialogContent(content);
            setConfigDialogOpen(true);
        };

        if (error) {
            return (
                <Dialog open={open} onClose={onClose}>
                    <DialogTitle>Error</DialogTitle>
                    <DialogContent>
                        <Typography color="error">{error}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>Close</Button>
                    </DialogActions>
                </Dialog>
            );
        }

        if (loading || !poDetails || !poDetails.data || !poDetails.data.header) {
            return (
                <Dialog open={open} onClose={onClose}>
                    <DialogTitle>Loading...</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>Close</Button>
                    </DialogActions>
                </Dialog>
            );
        }

        const { header, lines } = poDetails.data;
        
        // Debug: Log the header structure
        console.log('🔍 PO Header Data Structure:', header);
        console.log('🔍 Available header fields:', Object.keys(header));
        
        // Pre-process the lines to add events_summary
        const processedLines = lines.map(line => ({
            ...line,
            events_summary: [
                line.acknowledgment?.date ? `Ack: ${new Date(line.acknowledgment.date).toLocaleDateString()} | Qty: ${line.acknowledgment.quantity || 'N/A'}` : null,
                line.shipment?.date ? `Ship: ${new Date(line.shipment.date).toLocaleDateString()} | Qty: ${line.shipment.quantity || 'N/A'}` : null,
                line.receipt?.date ? `Rcpt: ${new Date(line.receipt.date).toLocaleDateString()} | Qty: ${line.receipt.quantity || 'N/A'}` : null
            ].filter(Boolean).join('\n')
        }));

        return (
            <Dialog 
                open={open} 
                onClose={onClose}
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="h6">
                                Purchase Order Details - {header.po_number}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                <Chip 
                                    label={header.status}
                                    size="small"
                                    sx={{ 
                                        backgroundColor: statusColors[header.status] || '#9e9e9e',
                                        color: 'white'
                                    }}
                                />
                                {header.optimization_run_id && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Optimization Run:
                                        </Typography>
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: '#1976d2', 
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                                fontWeight: 'bold'
                                            }}
                                            onClick={() => handleOpenOptRunDialog(header.optimization_run_id)}
                                        >
                                            {header.optimization_run_id}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        <IconButton onClick={onClose}>
                            <InfoOutlinedIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={3}>
                        {/* Planning Workflow Tracer */}
                        <Grid item xs={12}>
                            <Paper sx={{ p: 2, mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
                                    <Typography variant="h6">Planning Workflow</Typography>
                                </Box>
                                
                                {workflowLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : (
                                    <Stepper activeStep={activeStep} orientation="horizontal">
                                        {planningSteps.map((step, index) => {
                                            const stepData = planningWorkflowData[step.key];
                                            const isCompleted = stepData !== null;
                                            const isExpanded = expandedStep === index;
                                            
                                            // Special logic for PO Gateway stage
                                            let isGatewayReleased = false;
                                            if (step.key === 'po_gateway' && stepData) {
                                                // Check if PO has been released to gateway
                                                // Status can be: SENT, ACKNOWLEDGED, SHIPPED, RECEIVED (all indicate gateway release)
                                                // Or specific gateway status if available
                                                isGatewayReleased = stepData.status === 'COMPLETED' || 
                                                                   stepData.gateway_status === 'SENT' || 
                                                                   stepData.gateway_status === 'ACKNOWLEDGED' ||
                                                                   stepData.status === 'SENT' ||
                                                                   stepData.status === 'ACKNOWLEDGED' ||
                                                                   stepData.status === 'SHIPPED' ||
                                                                   stepData.status === 'RECEIVED';
                                            }
                                            
                                            return (
                                                <Step key={step.key} completed={isCompleted}>
                                                    <StepLabel
                                                        onClick={() => handleStepClick(index)}
                                                        sx={{ 
                                                            cursor: 'pointer',
                                                            '&:hover': { opacity: 0.8 }
                                                        }}
                                                        StepIconComponent={() => (
                                                            <Box
                                                                sx={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: step.key === 'po_gateway' 
                                                                        ? (isGatewayReleased ? step.color : '#e0e0e0')
                                                                        : (isCompleted ? step.color : '#e0e0e0'),
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: 'white'
                                                                }}
                                                            >
                                                                {step.key === 'po_gateway' 
                                                                    ? <RouterIcon />
                                                                    : (isCompleted ? step.icon : <ErrorIcon />)
                                                                }
                                                            </Box>
                                                        )}
                                                    >
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {step.key === 'po_gateway' ? (
                                                                    <>
                                                                        <Typography variant="subtitle2">
                                                                            PO Gateway
                                                                        </Typography>
                                                                        {isGatewayReleased ? (
                                                                            <CheckCircleIcon sx={{ color: step.color, fontSize: 16 }} />
                                                                        ) : (
                                                                            <CloseIcon sx={{ color: '#e0e0e0', fontSize: 16 }} />
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Typography variant="subtitle2">
                                                                            {step.label}
                                                                        </Typography>
                                                                        {isCompleted && (
                                                                            <CheckCircleIcon sx={{ color: step.color, fontSize: 16 }} />
                                                                        )}
                                                                    </>
                                                                )}
                                                            </Box>
                                                            
                                                                                                                        {/* Timestamp display */}
                                                            {stepData && (() => {
                                                                let timestamp = null;
                                                                
                                                                switch (step.key) {
                                                                    case 'forecast':
                                                                        timestamp = stepData.run_timestamp;
                                                                        break;
                                                                    case 'optimization':
                                                                        timestamp = stepData.run_metadata?.created_at;
                                                                        break;
                                                                    case 'sourcing':
                                                                        // Use released_at timestamp for sourcing
                                                                        timestamp = stepData.released_at;
                                                                        break;
                                                                    case 'po_generation':
                                                                        // Use released_at timestamp for PO generation
                                                                        timestamp = stepData.released_at;
                                                                        break;
                                                                    case 'po_gateway':
                                                                        // Only show timestamp when PO status is SENT
                                                                        timestamp = stepData.gateway_status === 'SENT' ? stepData.released_at : null;
                                                                        break;
                                                                    default:
                                                                        timestamp = null;
                                                                }
                                                                
                                                                if (timestamp) {
                                                                    const date = new Date(timestamp);
                                                                    const timestampText = date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                                                    return (
                                                                        <Typography 
                                                                            variant="caption" 
                                                                            sx={{ 
                                                                                color: 'text.secondary',
                                                                                fontSize: '0.7rem',
                                                                                textAlign: 'center',
                                                                                maxWidth: '120px',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                whiteSpace: 'nowrap'
                                                                            }}
                                                                        >
                                                                            {timestampText}
                                                                        </Typography>
                                                                    );
                                                                }
                                                                
                                                                return null; // Don't render anything if no timestamp
                                                            })()}
                                                        </Box>
                                                    </StepLabel>
                                                    
                                                    {isExpanded && (
                                                        <StepContent>
                                                            <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                                                                <Typography variant="subtitle2" gutterBottom>
                                                                    {step.label} Details
                                                                </Typography>
                                                                
                                                                {stepData ? (
                                                                    <>
                                                                        {step.key === 'forecast' && (
                                                                            <Box>
                                                                                <Typography variant="body2">
                                                                                    <strong>Run ID:</strong> {stepData.run_id || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Algorithm:</strong> {stepData.algorithm_name || 'N/A'} {stepData.algorithm_version ? `v${stepData.algorithm_version}` : ''}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Status:</strong> {stepData.status || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Grain:</strong> {stepData.forecast_grain || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Forecast Period:</strong> {stepData.forecast_start_date ? `${stepData.forecast_start_date} to ${stepData.forecast_end_date}` : 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Created:</strong> {stepData.run_timestamp ? new Date(stepData.run_timestamp).toLocaleString() : 'N/A'}
                                                                                </Typography>
                                                                                
                                                                                <Button
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    sx={{ mt: 1 }}
                                                                                    onClick={() => handleShowConfig('Forecast Configuration', stepData.parameters || {})}
                                                                                >
                                                                                    View Configuration
                                                                                </Button>
                                                                            </Box>
                                                                        )}
                                                                        
                                                                        {step.key === 'optimization' && (
                                                                            <Box>
                                                                                <Typography variant="body2">
                                                                                    <strong>Run ID:</strong> {stepData.run_metadata?.inventory_run_id || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Status:</strong> {stepData.run_metadata?.status || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Stores:</strong> {stepData.scope?.store_count || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Products:</strong> {stepData.scope?.active_products_count || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>SKUs Processed:</strong> {stepData.scope?.total_skus_processed || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Successful SKUs:</strong> {stepData.scope?.successful_skus_count || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Processing Time:</strong> {stepData.run_metadata?.processing_time_seconds ? `${stepData.run_metadata.processing_time_seconds.toFixed(1)}s` : 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Created:</strong> {stepData.run_metadata?.created_at ? new Date(stepData.run_metadata.created_at).toLocaleString() : 'N/A'}
                                                                                </Typography>
                                                                                
                                                                                <Button
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    sx={{ mt: 1, mr: 1 }}
                                                                                    onClick={() => handleShowConfig('Inventory Parameters', stepData.configuration?.inventory_parameters || {})}
                                                                                >
                                                                                    View Parameters
                                                                                </Button>
                                                                                <Button
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    onClick={() => handleOpenOptRunDialog(stepData.run_metadata?.inventory_run_id)}
                                                                                >
                                                                                    View Details
                                                                                </Button>
                                                                            </Box>
                                                                        )}
                                                                        
                                                                        {step.key === 'sourcing' && (
                                                                            <Box>
                                                                                <Typography variant="body2">
                                                                                    <strong>Run ID:</strong> {stepData.run_id}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Algorithm:</strong> {stepData.algorithm || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Grouping Strategy:</strong> {stepData.grouping || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Default Lead Time:</strong> {stepData.lead_time || 'N/A'} days
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Max Suppliers per SKU:</strong> {stepData.config?.global_defaults?.allocation?.max_suppliers_per_sku || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Primary Supplier %:</strong> {(stepData.config?.global_defaults?.allocation?.primary_supplier_percentage * 100) || 'N/A'}%
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Max PO Value:</strong> ${stepData.config?.po_generation_config?.max_po_value?.toLocaleString() || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Results Path:</strong> {stepData.results_path || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Completed:</strong> {stepData.released_at ? new Date(stepData.released_at).toLocaleString() : 'N/A'}
                                                                                </Typography>
                                                                                
                                                                                <Button
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    sx={{ mt: 1, mr: 1 }}
                                                                                    onClick={() => handleShowConfig('Sourcing Configuration', stepData.config)}
                                                                                >
                                                                                    View Configuration
                                                                                </Button>
                                                                                <Button
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    onClick={() => handleShowConfig('Department Rules', stepData.config?.department_rules || [])}
                                                                                >
                                                                                    View Department Rules
                                                                                </Button>
                                                                            </Box>
                                                                        )}
                                                                        
                                                                        {step.key === 'po_generation' && (
                                                                            <Box>
                                                                                <Typography variant="body2">
                                                                                    <strong>Run ID:</strong> {stepData.run_id}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Status:</strong> {stepData.status}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Grouping Strategy:</strong> {stepData.config?.grouping_strategy || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Max PO Value:</strong> ${stepData.config?.max_po_value?.toLocaleString() || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Min PO Value:</strong> ${stepData.config?.min_po_value?.toLocaleString() || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Max Lines per Order:</strong> {stepData.config?.max_po_lines_per_order || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Default Lead Time:</strong> {stepData.config?.default_lead_time_days || 'N/A'} days
                                                                                </Typography>
                                                                                
                                                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                                                    <strong>Generated:</strong> {stepData.released_at ? new Date(stepData.released_at).toLocaleString() : 'N/A'}
                                                                                </Typography>
                                                                                
                                                                                <Button
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    sx={{ mt: 1 }}
                                                                                    onClick={() => handleShowConfig('PO Generation Configuration', stepData.config)}
                                                                                >
                                                                                    View Configuration
                                                                                </Button>
                                                                            </Box>
                                                                        )}
                                                                        
                                                                        {step.key === 'po_gateway' && (
                                                                            <Box>
                                                                                <Typography variant="body2">
                                                                                    <strong>Status:</strong> {stepData.status || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Gateway Status:</strong> {stepData.gateway_status || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Released At:</strong> {stepData.released_at ? new Date(stepData.released_at).toLocaleString() : 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>PO Number:</strong> {header.po_number || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Supplier:</strong> {header.supplier?.supplier_name || header.supplier_id || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Total Amount:</strong> ${header.financial?.total_amount?.toLocaleString(undefined, {
                                                                                        minimumFractionDigits: 2,
                                                                                        maximumFractionDigits: 2
                                                                                    }) || header.total_amount?.toLocaleString(undefined, {
                                                                                        minimumFractionDigits: 2,
                                                                                        maximumFractionDigits: 2
                                                                                    }) || 'N/A'}
                                                                                </Typography>
                                                                                
                                                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                                                    <strong>Last Updated:</strong> {new Date().toLocaleString()}
                                                                                </Typography>
                                                                                
                                                                                <Button
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    sx={{ mt: 1 }}
                                                                                    onClick={() => handleShowConfig('PO Gateway Configuration', {
                                                                                        po_number: header.po_number,
                                                                                        status: stepData.status,
                                                                                        gateway_status: stepData.gateway_status,
                                                                                        released_at: stepData.released_at,
                                                                                        supplier: header.supplier?.supplier_name || header.supplier_id,
                                                                                        total_amount: header.financial?.total_amount || header.total_amount
                                                                                    })}
                                                                                >
                                                                                    View Gateway Details
                                                                                </Button>
                                                                            </Box>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <Box>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            No data available for this step.
                                                                        </Typography>
                                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                                            This could mean:
                                                                        </Typography>
                                                                        <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2 }}>
                                                                            <li>The step hasn't been completed yet</li>
                                                                            <li>There was an error fetching the data</li>
                                                                            <li>The optimization run ID is not available</li>
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </Paper>
                                                        </StepContent>
                                                    )}
                                                </Step>
                                            );
                                        })}
                                    </Stepper>
                                )}
                            </Paper>
                        </Grid>

                        {/* Header Information */}
                        <Grid item xs={12}>
                            <Paper sx={{ p: 2, mb: 2 }}>
                                <Typography variant="h6" gutterBottom>Order Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="subtitle2">Store</Typography>
                                        <Typography>
                                            {header.store?.store_name || header.store_id || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="subtitle2">Supplier</Typography>
                                        <Typography>
                                            {header.supplier?.supplier_name || header.supplier_id || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="subtitle2">Order Date</Typography>
                                        <Typography>
                                            {header.dates?.order_date ? 
                                                new Date(header.dates.order_date).toLocaleDateString() : 
                                                header.order_date ? 
                                                    new Date(header.order_date).toLocaleDateString() : 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="subtitle2">Expected Delivery</Typography>
                                        <Typography>
                                            {header.dates?.expected_delivery ? 
                                                new Date(header.dates.expected_delivery).toLocaleDateString() :
                                                header.expected_delivery ?
                                                    new Date(header.expected_delivery).toLocaleDateString() : 'Not specified'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="subtitle2">Total Amount</Typography>
                                        <Typography>
                                            {header.financial?.total_amount ? 
                                                `$${header.financial.total_amount.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}` : 
                                                header.total_amount ?
                                                    `$${header.total_amount.toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                    })}` : 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="subtitle2">Payment Terms</Typography>
                                        <Typography>
                                            {header.financial?.payment_terms || header.payment_terms || 'Standard'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                        
                        {/* Line Items */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>Line Items</Typography>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{
                                            bgcolor: 'grey.50',
                                            '& .MuiTableCell-head': {
                                                color: 'text.secondary',
                                                fontWeight: 600,
                                                fontSize: '0.875rem',
                                                borderBottom: '2px solid',
                                                borderColor: 'divider',
                                                py: 1.5
                                            }
                                        }}>
                                            <TableCell>Line #</TableCell>
                                            <TableCell>SKU</TableCell>
                                            <TableCell>Product</TableCell>
                                            <TableCell>Department</TableCell>
                                            <TableCell align="right">Quantity</TableCell>
                                            <TableCell align="right">Unit Cost</TableCell>
                                            <TableCell align="right">Total Cost</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Receipt Date</TableCell>
                                            <TableCell>Events</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {processedLines.map((line, index) => (
                                            <TableRow 
                                                key={index}
                                                sx={{
                                                    '&:hover': {
                                                        bgcolor: 'action.hover',
                                                    },
                                                    '& .MuiTableCell-body': {
                                                        py: 1.25
                                                    }
                                                }}
                                            >
                                                <TableCell>{line.line_number}</TableCell>
                                                <TableCell>{line.sku}</TableCell>
                                                <TableCell>{line.category}</TableCell>
                                                <TableCell>{line.department}</TableCell>
                                                <TableCell align="right">{line.quantity?.toLocaleString() || '0'}</TableCell>
                                                <TableCell align="right">
                                                    ${line.unit_cost?.toFixed(2) || '0.00'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    ${line.total_cost?.toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                    }) || '0.00'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={line.status}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: statusColors[line.status] || '#9e9e9e',
                                                            color: 'white'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {line.receipt?.date ? 
                                                        new Date(line.receipt.date).toLocaleDateString() : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                                        {line.events_summary}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    };

    // Configuration Dialog Component
    const ConfigurationDialog = () => (
        <Dialog
            open={configDialogOpen}
            onClose={() => setConfigDialogOpen(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">{configDialogTitle}</Typography>
                    <IconButton onClick={() => setConfigDialogOpen(false)}>
                        <InfoOutlinedIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box 
                    component="pre" 
                    sx={{ 
                        whiteSpace: 'pre-wrap', 
                        wordWrap: 'break-word',
                        bgcolor: 'grey.100',
                        p: 2,
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        maxHeight: '60vh',
                        overflow: 'auto'
                    }}
                >
                    {typeof configDialogContent === 'object' 
                        ? JSON.stringify(configDialogContent, null, 2)
                        : configDialogContent || 'No configuration data available'
                    }
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setConfigDialogOpen(false)}>Close</Button>
            </DialogActions>
        </Dialog>
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
            <Typography variant="h4" sx={{ mb: 2 }}>Buying Desk</Typography>
            
            {/* Filter Controls */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    label="PO Number"
                    value={filters.po_number}
                    onChange={(e) => handleFilterChange('po_number', e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: filters.po_number && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => handleFilterChange('po_number', '')}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                <TextField
                    size="small"
                    label="Store Name"
                    value={filters.store_name}
                    onChange={(e) => handleFilterChange('store_name', e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: filters.store_name && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => handleFilterChange('store_name', '')}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                <TextField
                    size="small"
                    label="Supplier"
                    value={filters.supplier_name}
                    onChange={(e) => handleFilterChange('supplier_name', e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: filters.supplier_name && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => handleFilterChange('supplier_name', '')}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={filters.status}
                        label="Status"
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                        <MenuItem value="">All Statuses</MenuItem>
                        {uniqueStatuses.map(status => (
                            <MenuItem key={status} value={status}>{status}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={clearFilters}
                    startIcon={<ClearIcon />}
                >
                    Clear Filters
                </Button>
            </Box>
            
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{
                            bgcolor: 'grey.50',
                            '& .MuiTableCell-head': {
                                color: 'text.secondary',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                borderBottom: '2px solid',
                                borderColor: 'divider',
                                py: 1.5
                            }
                        }}>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.id === 'total_amount' || column.id === 'total_ordered' || 
                                          column.id === 'total_shipped' || column.id === 'total_received' ? 'right' : 'left'}
                                    sortDirection={orderBy === column.id ? order : false}
                                >
                                    {column.sortable ? (
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : 'asc'}
                                            onClick={() => handleRequestSort(column.id)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : (
                                        column.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((row, index) => {
                                const events = [
                                    {
                                        type: 'Acknowledgment',
                                        data: row.acknowledgment
                                    },
                                    {
                                        type: 'Shipment',
                                        data: row.shipment
                                    },
                                    {
                                        type: 'Receipt',
                                        data: row.receipt
                                    }
                                ];

                                return (
                                    <TableRow 
                                        key={index}
                                        sx={{
                                            '&:hover': {
                                                bgcolor: 'action.hover',
                                            },
                                            '& .MuiTableCell-body': {
                                                py: 1.25
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <span 
                                                style={{ 
                                                    color: '#1976d2', 
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline'
                                                }}
                                                onClick={() => fetchPODetails(row.po_number)}
                                            >
                                                {row.po_number}
                                            </span>
                                        </TableCell>
                                        <TableCell>{row.store?.store_id}</TableCell>
                                        <TableCell>{row.store?.store_name}</TableCell>
                                        <TableCell>{row.supplier?.supplier_name}</TableCell>
                                        <TableCell>
                                            {row.dates?.order_date ? 
                                                new Date(row.dates.order_date).toLocaleDateString() : 'N/A'}
                                        </TableCell>
                                        <TableCell>
                                            <StatusCell value={row.status} />
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.financial?.total_amount ? 
                                                `$${row.financial.total_amount.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}` : 'N/A'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.items?.total_ordered?.toLocaleString() || '0'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.items?.total_shipped?.toLocaleString() || '0'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.items?.total_received?.toLocaleString() || '0'}
                                        </TableCell>
                                        <TableCell>
                                            {row.optimization_run_id ? (
                                                <span 
                                                    style={{ 
                                                        color: '#1976d2', 
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline'
                                                    }}
                                                    onClick={() => handleOpenOptRunDialog(row.optimization_run_id)}
                                                >
                                                    {row.optimization_run_id}
                                                </span>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ 
                                                display: 'flex', 
                                                flexDirection: 'column',
                                                gap: '4px'
                                            }}>
                                                {events.map(event => {
                                                    if (!event.data) return null;
                                                    const { date, quantity } = event.data;
                                                    
                                                    if (!date && !quantity) return null;

                                                    return (
                                                        <div key={event.type}>
                                                            {`${event.type}: ${date ? new Date(date).toLocaleDateString() : 'N/A'} | Qty: ${quantity?.toLocaleString() || 'N/A'}`}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    {loading ? 'Loading...' : 'No matching orders found'}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={filteredAndSortedData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            
            {/* PO Details Dialog */}
            <PODetailsDialog 
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                poDetails={selectedPO}
                loading={detailsLoading}
                error={detailsError}
            />
            
            {/* Optimization Run Details Dialog */}
            {selectedOptRunId && (
                <OptimizationRunDetailsDialog
                    open={optRunOpen}
                    onClose={handleCloseOptRunDialog}
                    runId={selectedOptRunId}
                />
            )}
            
            {/* Configuration Dialog */}
            <ConfigurationDialog />
        </Box>
    );
};

export default Orders; 